/* QubCommon Methods */
import { CollectionView } from "@grapecity/wijmo";
import { CellRange } from "@grapecity/wijmo.grid";
import { FlexGridXlsxConverter } from "@grapecity/wijmo.grid.xlsx";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { getData } from "src/apis/controllers/apiEntities";
import { useLoading } from "src/hooks/useLoading";
import {
  ModalButtons,
  ModalImages,
  useModal,
} from "src/qubCommon/context/modalContext";
import {
  formatCritFldSearch,
  formatValuesListSearch,
} from "src/qubCommon/helperMethods/formatRequest";
import {
  applySelectionMapToFresh,
  selectionMapFromCachedList,
} from "src/qubCommon/helperMethods/applySelectionMap";

const CRITERIA_STORAGE_PREFIXES = {
  extraParams: "extraParams",
  critFields: "critFields",
  critValues: "critValues",
};

const getUserCriteriaScope = (authState) =>
  authState?.user?.id ??
  authState?.user?.userId ??
  authState?.user?.username ??
  authState?.user?.email ??
  "default";

const getExplorerCriteriaStorageKeys = ({
  explorerDataKey,
  userScope,
  critId,
}) => {
  if (!explorerDataKey) return null;

  const keySuffix = [explorerDataKey, userScope || "default", critId || "default"]
    .map((value) => String(value))
    .join("_");

  return {
    extraParams: `${CRITERIA_STORAGE_PREFIXES.extraParams}_${keySuffix}`,
    critFields: `${CRITERIA_STORAGE_PREFIXES.critFields}_${keySuffix}`,
    critValues: `${CRITERIA_STORAGE_PREFIXES.critValues}_${keySuffix}`,
  };
};

const canUseLocalStorage = () => {
  try {
    return typeof window !== "undefined" && Boolean(window.localStorage);
  } catch {
    return false;
  }
};

const getCachedValue = (cachedField, property, fallback) =>
  Object.prototype.hasOwnProperty.call(cachedField, property)
    ? cachedField[property]
    : fallback;

const applyCachedFieldSearchToFresh = (freshFields = [], cachedFields = []) => {
  if (!Array.isArray(freshFields)) return cachedFields;

  const cachedFieldsByName = new Map(
    cachedFields
      ?.filter((field) => field?.fldSearchName)
      .map((field) => [field.fldSearchName, field])
  );

  return freshFields.map((field) => {
    const cachedField = cachedFieldsByName.get(field?.fldSearchName);
    if (!cachedField) return field;

    return {
      ...field,
      defaultOperator: getCachedValue(
        cachedField,
        "defaultOperator",
        field.defaultOperator
      ),
      value1: getCachedValue(cachedField, "value1", field.value1),
      value2: getCachedValue(cachedField, "value2", field.value2),
    };
  });
};

const readStorageJson = (key) => {
  if (!key || !canUseLocalStorage()) return null;

  const cachedValue = window.localStorage.getItem(key);
  if (!cachedValue) return null;

  try {
    return JSON.parse(cachedValue);
  } catch (error) {
    console.log("Invalid explorer criteria cache", error);
    window.localStorage.removeItem(key);
    return null;
  }
};

const persistExplorerCriteria = ({ storageKeys, params, criteria }) => {
  if (!storageKeys || !canUseLocalStorage()) return;

  try {
    window.localStorage.setItem(
      storageKeys.extraParams,
      JSON.stringify(params ?? {})
    );
    window.localStorage.setItem(
      storageKeys.critFields,
      JSON.stringify(criteria?.critFldsSearch ?? [])
    );
    window.localStorage.setItem(
      storageKeys.critValues,
      JSON.stringify(criteria?.critValuesList ?? [])
    );
  } catch (error) {
    console.log("Could not save explorer criteria cache", error);
  }
};

const restoreExplorerCriteria = ({ storageKeys, criteria, resetForm }) => {
  if (!storageKeys || !criteria) return;

  const extraParams = readStorageJson(storageKeys.extraParams);
  if (
    extraParams &&
    typeof extraParams === "object" &&
    !Array.isArray(extraParams)
  ) {
    resetForm({ ...extraParams, pageNumber: 1 });
  }

  const critFields = readStorageJson(storageKeys.critFields);
  if (Array.isArray(critFields)) {
    criteria.critFldsSearch = applyCachedFieldSearchToFresh(
      criteria.critFldsSearch,
      critFields
    );
  }

  const critValues = readStorageJson(storageKeys.critValues);
  if (!Array.isArray(critValues)) return;

  if (Array.isArray(criteria.critValuesList)) {
    criteria.critValuesList = applySelectionMapToFresh(
      criteria.critValuesList,
      selectionMapFromCachedList(critValues)
    );
    return;
  }

  criteria.critValuesList = critValues;
};

const removeExplorerCriteriaStorage = (storageKeys) => {
  if (!storageKeys || !canUseLocalStorage()) return;

  Object.values(storageKeys).forEach((key) => {
    window.localStorage.removeItem(key);
  });
};

const useExplorerGrid = (props) => {
  const {
    status,
    explorerDataKey,
    explorerDataKeyParams,
    xlsxName = "Report",
    navUrl = "",
    onSelect,
    onPopup,
    additionalCritFldsSearch,
    skipSavedCriteria = false,
    criteriaSearchParamMap,
  } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const authState = useSelector((state) => state.auth);
  const userCriteriaScope = getUserCriteriaScope(authState);
  const form = useForm({ defaultValues: { pageNumber: 1 } });
  const { reset, handleSubmit, formState } = form;
  const { isDirty } = formState;
  const [searchParams, setSearchParams] = useSearchParams();
  const critId = searchParams.get("critId");
  const criteriaStorageKeys = getExplorerCriteriaStorageKeys({
    explorerDataKey,
    userScope: userCriteriaScope,
    critId,
  });
  const { openModal } = useModal();
  const [gridData, setGridData] = useState(null);
  const [explParams, setExplParams] = useState(null);
  const [valueList, setValueList] = useState([]);
  const [valueListChips, setValueListChips] = useState([]);
  const [fieldSearchChips, setFieldSearchChips] = useState([]);
  const gridRef = useRef();
  const mobileGridRef = useRef();
  const toolbarRef = useRef();
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const [records, setRecords] = useState({
    returned: null,
    total: null,
    filtered: null,
  });

  const [contextMenuAnchor, setContextMenuAnchor] = useState(null);

  const [contextMenuData, setContextMenuData] = useState(null);
  const [contextMenuCellData, setContextMenuCellData] = useState(null);
  const [contextMenuAllData, setContextMenuAllData] = useState(null);
  const [contextMenuNavigationId, setContextMenuNavigationId] = useState(null);
  const [groupDescriptions, setGroupDescriptions] = useState(false);
  /* useEffect(() => {
        console.log('useExplorerGrid toolbar', toolbarRef.current.criteriaRef?.current?.critValuesList);
        setValueList(toolbarRef.current.criteriaRef?.current?.critValuesList);
    }, [toolbarRef?.current?.criteriaRef?.current?.critFldsSearch, toolbarRef?.current?.criteriaRef?.current?.critValuesList]);

    useEffect(() => {
        const helpChips = [];
        if (valueList && valueList.length > 0) {
            valueList.forEach((valueList) =>
                valueList?.children.forEach((c) => {
                    if (c.isSelected) helpChips.push(c);
                })
            );
        }
        setValueListChips(helpChips.map((chip) => chip?.title));
    }, [valueList]);

    useEffect(() => {
        console.log('useExplorerGrid valueListChips', valueListChips);
    }, [valueListChips]); */

  /* Get the quick access filters */
  useEffect(() => {
    const fetch = async () => {
      if (explorerDataKeyParams) {
        const res = await getData({ dataKey: explorerDataKeyParams });
        setExplParams(res?.data);
      }
    };
    fetch();
  }, [explorerDataKeyParams]);

  /* Clear grid when criteria change */
  useEffect(() => {
    if (isDirty) setGridData({ items: [] });
  }, [isDirty]);

  /* Initialize the grid */
  const gridInitialized = (flex) => {
    gridRef.current = flex;
    flex.selectionMode = "None";

    flex.itemFormatter = (panel, r, c, cell) => {
      if (panel.cellType === 3) {
        cell.innerHTML = `<div class='row-header'> ${panel.rows[r]?.index + 1
          }</div>`;
      }
    };

    flex.formatItem.addHandler((s, e) => {
      if (e.panel.cellType === 3) {
        e.cell.innerHTML = `<div class='row-header'> ${e.panel.rows[e.row]?.index + 1
          }</div>`;
      }

      // if (e.panel === flex.topLeftCells) {
      //     e.cell.outerHTML =
      //         '<div class="wj-cell wj-header wj-align-center top-left" tabindex="-1" style="left: 0px; top: 0px; width: 44px; height: 35px;"></div>';
      // }
    });

    flex.resizedColumn.addHandler((s, e) => {
      const col = e.panel.columns[e.col];
      // console.log(col.width);
      getData({
        dataKey: "Qub-ColsWidth-Save",
        parameters: {
          fldName: col.binding,
          fldWidth: col.width,
          explorerDataKey,
        },
      });
    });

    /* Enable selection on single click */
    flex.hostElement.addEventListener("mousedown", (e) => {
      if (flex.selectionMode === 0) {
        const ht = flex.hitTest(e);
        flex.selectionMode = "ListBox";
        flex.select(ht.row, 0);
        flex.select(ht.row, 0);
      }
    });

    /* navigatte on double click */
    flex?.hostElement?.addEventListener("dblclick", (e) => {
      const ht = flex.hitTest(e.pageX, e.pageY);

      const currentItem = flex.rows[ht.row]?.dataItem;

      if (currentItem?.groupDescription?.propertyName) return;
      if (currentItem && ht.cellType === 1) {
        if (onSelect && typeof onSelect === "function")
          onSelect(currentItem, e.ctrlKey);
        else if (e.ctrlKey) {
          const newWindow = window.open(
            navUrl.replace("{:id}", currentItem?.Id),
            "_blank"
          );
          newWindow.focus();
        } else navigate(navUrl.replace("{:id}", currentItem?.Id));
      }
    });

    /* navigatte on Enter */
    flex.hostElement.addEventListener(
      "keydown",
      (e) => {
        if (e.key === "Enter") {
          if (onSelect && typeof onSelect === "function")
            onSelect(flex?.selectedItems[0], e.ctrlKey);
          else if (e.ctrlKey) {
            const newWindow = window.open(
              navUrl.replace("{:id}", flex?.selectedItems[0].Id),
              "_blank"
            );
            newWindow.focus();
          } else if (flex?.selectedItems[0]?.Id)
            navigate(navUrl.replace("{:id}", flex?.selectedItems[0].Id));
        }
      },
      true
    );

    flex.hostElement.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const ht = flex.hitTest(e);
      if (flex.selectionMode === 0) flex.selectionMode = "ListBox";
      flex.select(ht.row, ht.col);
      const currentItem = flex?.collectionView?.currentItem;
      const visibleCurrentItem = {};
      if (currentItem && ht.cellType === 1) {
        flex.columns.forEach((c) => {
          const property = c?.binding;
          if (c?.visible)
            visibleCurrentItem[`${property}`] = currentItem[`${property}`];
        });
        setContextMenuNavigationId(currentItem?.Id);
        setContextMenuData(visibleCurrentItem);
        setContextMenuCellData(flex.cells.getCellData(ht.row, ht.col));
        setContextMenuAllData(
          flex.getClipString(
            new CellRange(0, 0, flex.rows.length - 1, flex.columns.length - 1),
            false,
            true
          )
        );
        console.log(flex.cells.getCellElement(ht.row, ht.col))
        setContextMenuAnchor(flex.cells.getCellElement(ht.row, ht.col));
        if (onPopup && typeof onPopup === "function") onPopup(currentItem);
      }
    });

    //
    // make rows taller on phone layout
    //
    // hide column headers on narrow layouts
  };

  /* Handle fetch data */
  const fetchData = async (data) => {
    const res = await getData({ dataKey: explorerDataKey, parameters: data });
    return res;
  };

  const getAdditionalCritFldsSearch = (data) => {
    if (typeof additionalCritFldsSearch === "function") {
      return additionalCritFldsSearch(data) ?? [];
    }

    return additionalCritFldsSearch ?? [];
  };

  const syncCriteriaSearchParams = (criteria) => {
    if (!criteriaSearchParamMap) return;

    const nextSearchParams = new URLSearchParams(searchParams);

    Object.entries(criteriaSearchParamMap).forEach(
      ([criteriaKey, searchParamKey]) => {
        const criteriaGroup = criteria?.critValuesList?.find(
          (group) => String(group?.value) === String(criteriaKey)
        );
        const selectedValues =
          criteriaGroup?.children
            ?.filter((child) => child?.isSelected)
            .map((child) => String(child.value)) ?? [];

        if (selectedValues.length > 0) {
          nextSearchParams.set(searchParamKey, selectedValues.join(","));
        } else {
          nextSearchParams.delete(searchParamKey);
        }
      }
    );

    if (nextSearchParams.toString() !== searchParams.toString()) {
      setSearchParams(nextSearchParams, { replace: true });
    }
  };

  /* Handle chips */
  const handleChips = () => {
    const helpValueListChips = [];
    const helpFieldSearchChips = [];
    if (
      toolbarRef.current?.criteriaRef?.current?.critValuesList &&
      toolbarRef.current?.criteriaRef?.current?.critValuesList.length > 0
    ) {
      toolbarRef.current?.criteriaRef?.current?.critValuesList.forEach(
        (valueList) => {
          if (valueList?.children) {
            valueList?.children.forEach((c) => {
              if (c.isSelected) helpValueListChips.push(c);
            });
          }
        }
      );
    }
    setValueListChips(helpValueListChips);
    if (
      toolbarRef.current?.criteriaRef?.current?.critFldsSearch &&
      toolbarRef.current?.criteriaRef?.current?.critFldsSearch.length > 0
    ) {
      toolbarRef.current?.criteriaRef?.current?.critFldsSearch.forEach(
        (fieldSearch) => {
          if (fieldSearch.value1) helpFieldSearchChips.push(fieldSearch);
        }
      );
    }
    setFieldSearchChips(helpFieldSearchChips);
  };

  /* Handle search */
  const onSubmit = async (data) => {
    setLoading(true);
    reset(data);
    setRecords({ returned: null, total: null, filtered: null });
    const criteria = toolbarRef.current?.criteriaRef?.current;

    syncCriteriaSearchParams(criteria);

    if (!skipSavedCriteria) {
      persistExplorerCriteria({
        storageKeys: criteriaStorageKeys,
        params: data,
        criteria,
      });
    }

    const formattedCritFldsSearch =
      formatCritFldSearch(criteria?.critFldsSearch) ?? [];
    data.critFldsSearch = [
      ...formattedCritFldsSearch,
      ...getAdditionalCritFldsSearch(data),
    ];
    data.critValuesList = formatValuesListSearch(criteria?.critValuesList);
    data = { ...data, critId };
    handleChips();
    const res = await fetchData(data);
    if (!res?.data)
      openModal({
        title: t("Explorer"),
        message: t("NoDataFound"),
        imageType: ModalImages.Info,
        buttons: ModalButtons.OK,
        callback: (command) => { },
      });

    const helpData = new CollectionView(res?.data, res?.groupDescriptions ? { groupDescriptions: res?.groupDescriptions } : {});
    setGroupDescriptions(res?.groupDescriptions?.length > 0 ? true : false);
    setRecords({
      returned: res?.ReturnedRecords ?? null,
      total: res?.TotalRecords ?? null,
      filtered: res?.FilteredRecords ?? null,
    });
    setGridData(helpData);
    setLoading(false);
  };

  useEffect(() => {
    if (status) {
      if (!skipSavedCriteria) {
        restoreExplorerCriteria({
          storageKeys: criteriaStorageKeys,
          criteria: toolbarRef.current?.criteriaRef?.current,
          resetForm: reset,
        });
      }
      handleSubmit(onSubmit)();
    }
  }, [status, explorerDataKey, userCriteriaScope, critId]);

  const clearSavedExplorerCriteria = () => {
    removeExplorerCriteriaStorage(criteriaStorageKeys);
  };

  /* Handle excel report */
  const excelHandleClick = () => {
    FlexGridXlsxConverter.saveAsync(
      gridRef.current,
      {
        includeColumnHeaders: true,
        includeStyles: false,
      },
      `${xlsxName}.xlsx`
    );
  };

  const handleCloseContextMenu = () => {
    setContextMenuAnchor(null);
  };

  const mobileGridLoadedRows = (flex) => {
    flex.hostElement.addEventListener("mousedown", (e) => {
      if (flex.selectionMode === 0) {
        const ht = flex.hitTest(e);
        flex.selectionMode = "ListBox";
        flex.select(ht.row, 0);
        flex.select(ht.row, 0);
      }
    });
    flex.hostElement.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const ht = flex.hitTest(e);
      if (flex.selectionMode === 0) flex.selectionMode = "ListBox";
      flex.select(ht.row, ht.col);
      const currentItem = flex?.collectionView?.currentItem?.allData;
      const visibleCurrentItem = {};
      if (currentItem && ht.cellType === 1) {
        gridRef.current.columns.forEach((c) => {
          const property = c?.binding;
          if (c?.visible)
            visibleCurrentItem[`${property}`] = currentItem[`${property}`];
        });
        setContextMenuData(visibleCurrentItem);
        // setContextMenuCellData(flex.cells.getCellData(ht.row, ht.col));
        // setContextMenuAllData(flex.getClipString(new CellRange(0, 0, flex.rows.length - 1, flex.columns.length - 1), false, true));
        setContextMenuAnchor(flex.cells.getCellElement(ht.row, ht.col));
        if (onPopup && typeof onPopup === "function") onPopup(currentItem);
      }
    });
    let lastTapTime = 0;

    flex.hostElement.addEventListener("touchstart", function (event) {
      const currentTime = new Date().getTime();
      const timeSinceLastTap = currentTime - lastTapTime;

      if (timeSinceLastTap < 300) {
        // Adjust the time threshold as needed
        // Double-tap detected
        const ht = flex.hitTest(event);

        const currentItem = flex.rows[ht.row]?.dataItem?.allData;
        if (currentItem && ht.cellType === 1) {
          if (onSelect && typeof onSelect === "function") onSelect(currentItem);
          else navigate(navUrl.replace("{:id}", currentItem?.Id));
        }
        event.preventDefault(); // Prevent default behavior (e.g., zooming)
      }

      lastTapTime = currentTime;
    });

    flex?.hostElement?.addEventListener("dblclick", (e) => {
      const ht = flex.hitTest(e.pageX, e.pageY);

      const currentItem = flex.rows[ht.row]?.dataItem?.allData;
      if (currentItem && ht.cellType === 1) {
        if (onSelect && typeof onSelect === "function")
          onSelect(currentItem, e.ctrlKey);
        else if (e.ctrlKey) {
          const newWindow = window.open(
            navUrl.replace("{:id}", currentItem?.Id),
            "_blank"
          );
          newWindow.focus();
        } else navigate(navUrl.replace("{:id}", currentItem?.Id));
      }
    });
  };

  return {
    gridInitialized,
    mobileGridLoadedRows,
    handleSubmit,
    onSubmit,
    excelHandleClick,
    fetchData,
    additionalCritFldsSearch,
    clearSavedExplorerCriteria,
    submitOnValueListChipDelete: Boolean(criteriaSearchParamMap),
    explorerDataKey,
    gridRef,
    mobileGridRef,
    explParams,
    toolbarRef,
    form,
    isDirty,
    gridData,
    valueListChips,
    setValueListChips,
    fieldSearchChips,
    setFieldSearchChips,
    records,
    navUrl,
    contextMenu: {
      contextMenuAnchor,
      contextMenuData,
      contextMenuCellData,
      contextMenuAllData,
      handleCloseContextMenu,
      contextMenuNavigationId,
    },
    groupDescriptions,
  };
};

export default useExplorerGrid;
